import React, { useState, useEffect } from 'react';
import VoiceRecorder from './VoiceRecorder';
import SentenceDisplay from './SentenceDisplay';
import DetailedFeedback from './DetailedFeedback';
import CustomDropdown from './CustomDropdown';
import PhonemePractice from './PhonemePractice';
import HindiPronunciationQuest from './HindiPronunciationQuest';
import Leaderboard from './Leaderboard';
import { HINDI_PHONEMES, HINDI_PHONEME_CATEGORIES } from '../data/hindiPhonemes';
import { useAuth } from '../contexts/AuthContext';
import './HindiPracticeMode.css';

// Phoneme to audio filename mapping
const phonemeToFilename: { [key: string]: string } = {
  // Vowels
  'ə': 'schwa.mp3',
  'aː': 'aa_long.mp3',
  'ɪ': 'i_short.mp3',
  'iː': 'ii_long.mp3',
  'ʊ': 'u_short.mp3',
  'uː': 'uu_long.mp3',
  'eː': 'e_long.mp3',
  'ɛː': 'ai_long.mp3',
  'oː': 'o_long.mp3',
  'ɔː': 'au_long.mp3',
  
  // Unaspirated Consonants
  'k': 'ka.mp3',
  'g': 'ga.mp3',
  'tʃ': 'cha.mp3',
  'dʒ': 'ja.mp3',
  'ʈ': 'ta_retroflex.mp3',
  'ɖ': 'da_retroflex.mp3',
  't̪': 'ta_dental.mp3',
  'd̪': 'da_dental.mp3',
  'p': 'pa.mp3',
  'b': 'ba.mp3',
  
  // Aspirated Consonants
  'kʰ': 'kha.mp3',
  'gʱ': 'gha.mp3',
  'tʃʰ': 'chha.mp3',
  'dʒʱ': 'jha.mp3',
  'ʈʰ': 'tha_retroflex.mp3',
  'ɖʱ': 'dha_retroflex.mp3',
  't̪ʰ': 'tha_dental.mp3',
  'd̪ʱ': 'dha_dental.mp3',
  'pʰ': 'pha.mp3',
  'bʱ': 'bha.mp3',
  
  // Nasal Consonants
  'ŋ': 'nga.mp3',
  'ɲ': 'nya.mp3',
  'ɳ': 'na_retroflex.mp3',
  'n': 'na.mp3',
  'm': 'ma.mp3',
  
  // Semivowels & Others
  'j': 'ya.mp3',
  'r': 'ra.mp3',
  'l': 'la.mp3',
  'ʋ': 'va.mp3',
  'ʃ': 'sha.mp3',
  'ʂ': 'sha_retroflex.mp3',
  's': 'sa.mp3',
  'h': 'ha.mp3',
  'ɽ': 'da_flap.mp3',
  'ɽʱ': 'dha_flap.mp3'
};

export interface HindiSentence {
  id: number;
  hindi: string;
  transliteration: string;
  english: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  referenceAudioUrl: string;
}

export interface AnalysisResult {
  overallAccuracyScore: number;
  fluencyScore: number;
  words: WordAnalysis[];
}

export interface WordAnalysis {
  word: string;
  accuracyScore: number;
  errorType: 'None' | 'Mispronunciation' | 'Omission' | 'Insertion';
  phonemes?: PhonemeAnalysis[];
}

export interface PhonemeAnalysis {
  phoneme: string;
  accuracyScore: number;
}

const HindiPracticeMode: React.FC = () => {
  console.log('=== HindiPracticeMode v2 DEPLOYED:', new Date().toISOString(), '===');
  const { getIdToken, user } = useAuth();
  console.log('Auth hook result - user:', user?.email || 'null');
  
  const [sentences, setSentences] = useState<HindiSentence[]>([]);
  const [currentSentence, setCurrentSentence] = useState<HindiSentence | null>(null);
  const [difficulty, setDifficulty] = useState<'all' | 'beginner' | 'intermediate' | 'advanced'>('beginner');
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string>('');
  const [practiceWord, setPracticeWord] = useState<string | null>(null);
  const [approvedAudioUrls, setApprovedAudioUrls] = useState<Map<string, string>>(new Map());
  const [practiceMode, setPracticeMode] = useState<'phonemes' | 'game'>('game');
  const [selectedPhoneme, setSelectedPhoneme] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Determine backend URL based on environment
  const backendUrl = window.location.hostname === 'localhost' 
    ? 'http://localhost:8080'
    : 'https://voice-coach-backend-406125116283.us-east1.run.app';

  // Load sentences from JSON file
  useEffect(() => {
    fetch('/hindi-sentences.json')
      .then(response => response.json())
      .then(data => {
        setSentences(data);
        // Set initial sentence based on difficulty
        const filtered = data.filter((s: HindiSentence) => 
          difficulty === 'all' || s.difficulty === difficulty
        );
        if (filtered.length > 0) {
          setCurrentSentence(filtered[0]);
        }
      })
      .catch(err => {
        console.error('Failed to load Hindi sentences:', err);
        setError('Failed to load practice sentences');
      });
  }, [difficulty]);

  // Separate effect to fetch approved audio when user is authenticated
  useEffect(() => {
    const fetchApprovedAudio = async () => {
      console.log('=== Audio Fetch Debug ===');
      console.log('User object:', user);
      console.log('User email:', user?.email);
      console.log('User UID:', user?.uid);
      
      // Only fetch if user is authenticated
      if (!user) {
        console.log('User not authenticated yet, skipping audio fetch');
        return;
      }

      try {
        console.log('User authenticated, attempting to get token...');
        const token = await getIdToken();
        console.log('Token result:', token ? `Token received (${token.length} chars)` : 'Token is null');
        
        if (!token) {
          console.error('Could not get auth token despite user being authenticated');
          console.error('User object exists but token is null');
          return;
        }
        
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        };
        
        console.log('Making API request to:', `${backendUrl}/api/sentences?status=approved&language=hi-IN`);
        console.log('Request headers:', {
          'Content-Type': headers['Content-Type'],
          'Authorization': `Bearer ${token.substring(0, 20)}...`
        });
        
        const response = await fetch(`${backendUrl}/api/sentences?status=approved&language=hi-IN`, {
          headers
        });
        
        console.log('Response status:', response.status);
        console.log('Response headers:', response.headers);
        
        if (response.ok) {
          const result = await response.json();
          const audioMap = new Map<string, string>();
          
          console.log('Successfully fetched approved sentences:', result);
          
          // Map approved audio URLs by sentence text
          result.sentences?.forEach((sentence: any) => {
            if (sentence.reference_audio_url) {
              console.log('Found audio for:', sentence.text);
              audioMap.set(sentence.text, sentence.reference_audio_url);
            }
          });
          
          if (audioMap.size > 0) {
            console.log(`Loaded ${audioMap.size} audio files`);
            setApprovedAudioUrls(audioMap);
          } else {
            console.log('No approved audio files found');
          }
        } else {
          console.error('Failed to fetch approved audio, status:', response.status);
          const errorText = await response.text();
          console.error('Error response body:', errorText);
        }
      } catch (err: any) {
        console.error('Error in fetchApprovedAudio:', err);
        console.error('Error details:', err?.message, err?.stack);
      }
    };

    console.log('useEffect triggered, user:', user?.email || 'not logged in');
    if (user) {
      fetchApprovedAudio();
    }
  }, [user, getIdToken, backendUrl]);

  // Filter sentences when difficulty changes
  useEffect(() => {
    if (sentences.length > 0) {
      const filtered = sentences.filter(s => 
        difficulty === 'all' || s.difficulty === difficulty
      );
      if (filtered.length > 0) {
        setCurrentSentence(filtered[0]);
        resetPractice();
      }
    }
  }, [difficulty, sentences]);

  const handleRecordingComplete = async (blob: Blob, url: string) => {
    setAudioBlob(blob);
    setAudioUrl(url);
    setIsAnalyzing(true);
    setError('');

    try {
      // Prepare form data for backend
      const formData = new FormData();
      formData.append('audio', blob, 'recording.webm');
      formData.append('referenceText', currentSentence?.hindi || '');
      formData.append('language', 'hi-IN');
      formData.append('sentenceId', currentSentence?.id.toString() || '');

      // Send to backend for analysis
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/pronunciation-analysis`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Analysis failed: ${response.statusText}`);
      }

      const result = await response.json();
      setAnalysisResult(result);
    } catch (err) {
      console.error('Pronunciation analysis failed:', err);
      // For now, generate mock data for testing
      setAnalysisResult(generateMockAnalysis(currentSentence?.hindi || ''));
      // setError('Analysis service temporarily unavailable. Showing demo results.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const generateMockAnalysis = (text: string): AnalysisResult => {
    const words = text.split(' ');
    return {
      overallAccuracyScore: 75 + Math.random() * 20,
      fluencyScore: 70 + Math.random() * 25,
      words: words.map(word => ({
        word,
        accuracyScore: 60 + Math.random() * 35,
        errorType: Math.random() > 0.7 ? 
          (Math.random() > 0.5 ? 'Mispronunciation' : 'None') : 
          'None',
        phonemes: Math.random() > 0.5 ? generateMockPhonemes(word) : undefined
      }))
    };
  };

  const generateMockPhonemes = (word: string): PhonemeAnalysis[] => {
    // Generate mock phonemes based on word length
    const phonemeCount = Math.min(word.length, 3 + Math.floor(Math.random() * 3));
    return Array.from({ length: phonemeCount }, (_, i) => ({
      phoneme: word[i] || 'a',
      accuracyScore: 50 + Math.random() * 45
    }));
  };

  const handleNextSentence = () => {
    const filtered = sentences.filter(s => 
      difficulty === 'all' || s.difficulty === difficulty
    );
    const currentIndex = filtered.findIndex(s => s.id === currentSentence?.id);
    const nextIndex = (currentIndex + 1) % filtered.length;
    setCurrentSentence(filtered[nextIndex]);
    resetPractice();
  };

  const handlePreviousSentence = () => {
    const filtered = sentences.filter(s => 
      difficulty === 'all' || s.difficulty === difficulty
    );
    const currentIndex = filtered.findIndex(s => s.id === currentSentence?.id);
    const prevIndex = currentIndex === 0 ? filtered.length - 1 : currentIndex - 1;
    setCurrentSentence(filtered[prevIndex]);
    resetPractice();
  };

  const resetPractice = () => {
    setAudioBlob(null);
    setAudioUrl('');
    setAnalysisResult(null);
    setError('');
    setPracticeWord(null);
  };

  const handleWordPractice = (word: string) => {
    setPracticeWord(word);
    // In a real implementation, this would load word-specific audio
  };

  const closePracticeWord = () => {
    setPracticeWord(null);
  };

  // Function to play phoneme audio
  const playPhonemeAudio = (phonemeSymbol: string) => {
    const filename = phonemeToFilename[phonemeSymbol];
    if (filename) {
      const audio = new Audio(`/audio/hindi-phonemes/${filename}`);
      audio.play().catch(error => {
        console.error(`Error playing audio for ${phonemeSymbol}:`, error);
        // Optionally show user-friendly error
        setError(`Audio file not available for this phoneme yet`);
        setTimeout(() => setError(''), 3000); // Clear error after 3 seconds
      });
    } else {
      console.warn(`No audio file mapping found for phoneme: ${phonemeSymbol}`);
    }
  };

  // Detect if user is on a mobile device
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           ('ontouchstart' in window) ||
           (navigator.maxTouchPoints > 0);
  };

  // Handle long press for mobile
  const handleLongPress = (phonemeSymbol: string) => {
    let pressTimer: NodeJS.Timeout | null = null;
    
    const start = () => {
      pressTimer = setTimeout(() => {
        setSelectedPhoneme(phonemeSymbol);
      }, 600); // 600ms for long press
    };
    
    const cancel = () => {
      if (pressTimer) {
        clearTimeout(pressTimer);
        pressTimer = null;
      }
    };

    return {
      onTouchStart: (e: React.TouchEvent) => {
        e.preventDefault(); // Prevent default touch behavior
        start();
      },
      onTouchEnd: cancel,
      onTouchCancel: cancel,
      onTouchMove: cancel,
      onMouseDown: () => {
        if (!isMobileDevice()) {
          start();
        }
      },
      onMouseUp: () => {
        if (!isMobileDevice()) {
          cancel();
        }
      },
      onMouseLeave: () => {
        if (!isMobileDevice()) {
          cancel();
        }
      }
    };
  };

  return (
    <div className="hindi-practice-mode">
      {/* Mode Selector Tabs */}
      <div className="practice-mode-tabs" style={{
        marginBottom: '30px',
        display: 'flex',
        justifyContent: 'center',
        gap: '20px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '10px'
      }}>
          <button
            className={`mode-tab ${practiceMode === 'phonemes' ? 'active' : ''}`}
            onClick={() => {
              setPracticeMode('phonemes');
              resetPractice();
            }}
            style={{
              padding: '15px 40px',
              backgroundColor: practiceMode === 'phonemes' ? '#667eea' : 'white',
              color: practiceMode === 'phonemes' ? 'white' : '#667eea',
              border: practiceMode === 'phonemes' ? 'none' : '2px solid #e0e0e0',
              borderRadius: '30px',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: practiceMode === 'phonemes' ? '0 4px 15px rgba(102, 126, 234, 0.3)' : 'none'
            }}
          >
            📚 Lessons
          </button>
          <button
            className={`mode-tab ${practiceMode === 'game' ? 'active' : ''}`}
            onClick={() => {
              setPracticeMode('game');
              resetPractice();
            }}
            style={{
              padding: '15px 40px',
              backgroundColor: practiceMode === 'game' ? '#667eea' : 'white',
              color: practiceMode === 'game' ? 'white' : '#667eea',
              border: practiceMode === 'game' ? 'none' : '2px solid #e0e0e0',
              borderRadius: '30px',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: practiceMode === 'game' ? '0 4px 15px rgba(102, 126, 234, 0.3)' : 'none'
            }}
          >
            🎮 Games
          </button>
        </div>

      {/* Phoneme Practice Mode */}
      {practiceMode === 'phonemes' && (
        <div className="phoneme-practice-section">
          <div style={{
            textAlign: 'center',
            marginBottom: '30px'
          }}>
            <h1 style={{
              fontSize: '2.5rem',
              color: '#2c3e50',
              marginBottom: '10px'
            }}>📚 Hindi Lessons</h1>
            <p style={{
              fontSize: '1.2rem',
              color: '#666'
            }}>Learn and practice individual Hindi sounds</p>
          </div>
          {selectedPhoneme && (
            <PhonemePractice
              phoneme={selectedPhoneme}
              onClose={() => setSelectedPhoneme(null)}
              language="Hindi"
              accent="Standard"
            />
          )}
          
          {!selectedPhoneme && (
            <>
              {/* Category Filter */}
              <div className="phoneme-category-filter" style={{ marginBottom: '20px' }}>
                <label>Filter by Category:</label>
                <CustomDropdown
                  id="category-select"
                  value={selectedCategory}
                  options={[
                    { value: 'all', label: 'All Categories' },
                    ...Object.entries(HINDI_PHONEME_CATEGORIES).map(([key, cat]) => ({
                      value: key,
                      label: cat.name
                    }))
                  ]}
                  onChange={(value) => setSelectedCategory(value)}
                />
              </div>
              
              {/* Phoneme Grid */}
              <div className="phoneme-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: '15px',
                marginTop: '20px'
              }}>
                {HINDI_PHONEMES
                  .filter(p => selectedCategory === 'all' || p.category === selectedCategory)
                  .map((phoneme) => {
                    // Create long press handlers for this specific phoneme
                    const longPressHandlers = handleLongPress(phoneme.symbol);
                    
                    // Override onTouchEnd to handle both tap and long press
                    const customTouchEnd = (e: React.TouchEvent) => {
                      // Call the long press cancel handler
                      longPressHandlers.onTouchEnd();
                      
                      // Play audio on tap (short press)
                      if (isMobileDevice()) {
                        // Small delay to distinguish from long press
                        setTimeout(() => {
                          playPhonemeAudio(phoneme.symbol);
                        }, 50);
                      }
                    };
                    
                    return (
                      <div
                        key={phoneme.symbol}
                        className="phoneme-card clickable-phoneme"
                        onClick={(e) => {
                          // Only play audio on click if not prevented by touch
                          if (!e.defaultPrevented && !isMobileDevice()) {
                            playPhonemeAudio(phoneme.symbol);
                          }
                        }}
                        onDoubleClick={() => {
                          // Only allow double-click on desktop
                          if (!isMobileDevice()) {
                            setSelectedPhoneme(phoneme.symbol);
                          }
                        }}
                        onTouchStart={longPressHandlers.onTouchStart}
                        onTouchEnd={customTouchEnd}
                        onTouchCancel={longPressHandlers.onTouchCancel}
                        onTouchMove={longPressHandlers.onTouchMove}
                        onMouseDown={longPressHandlers.onMouseDown}
                        onMouseUp={longPressHandlers.onMouseUp}
                        onMouseLeave={(e) => {
                          // Handle both long press cancel and hover effect
                          longPressHandlers.onMouseLeave();
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            playPhonemeAudio(phoneme.symbol);
                          }
                        }}
                        style={{
                          padding: '15px',
                          backgroundColor: HINDI_PHONEME_CATEGORIES[phoneme.category].color + '20',
                          border: `2px solid ${HINDI_PHONEME_CATEGORIES[phoneme.category].color}`,
                          borderRadius: '10px',
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.2s',
                          position: 'relative',
                          WebkitTouchCallout: 'none',
                          WebkitUserSelect: 'none',
                          userSelect: 'none'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onContextMenu={(e) => {
                          // Prevent context menu on long press for mobile
                          if (isMobileDevice()) {
                            e.preventDefault();
                          }
                        }}
                      >
                        {/* Audio icon indicator */}
                        <div style={{ 
                          position: 'absolute', 
                          top: '8px', 
                          right: '8px',
                          fontSize: '16px',
                          opacity: 0.7
                        }}>
                          🔊
                        </div>
                        
                        <div style={{ fontSize: '36px', fontWeight: 'bold' }}>
                          {phoneme.devanagari}
                        </div>
                        <div style={{ fontSize: '18px', margin: '5px 0' }}>
                          /{phoneme.symbol}/
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {phoneme.description}
                        </div>
                        <div style={{ fontSize: '11px', marginTop: '5px', color: '#888' }}>
                          {phoneme.examples[0]}
                        </div>
                      </div>
                    );
                  })}
              </div>
              
              {/* Phoneme Information */}
              <div className="phoneme-info" style={{
                marginTop: '30px',
                padding: '20px',
                backgroundColor: '#f5f5f5',
                borderRadius: '10px'
              }}>
                <h3>📚 About Hindi Phonemes</h3>
                <p>Hindi has a rich phonetic system with distinct sounds not found in English:</p>
                <ul>
                  <li><strong>Retroflex sounds (मूर्धन्य):</strong> Produced with the tongue curled back (ट, ठ, ड, ढ, ण)</li>
                  <li><strong>Aspirated consonants (महाप्राण):</strong> Pronounced with a puff of air (ख, घ, छ, झ, etc.)</li>
                  <li><strong>Dental vs. Retroflex:</strong> त/ट, द/ड have different tongue positions</li>
                  <li><strong>Nasals (नासिक्य):</strong> Five different nasal sounds (ङ, ञ, ण, न, म)</li>
                </ul>
                <p style={{ marginTop: '15px', fontStyle: 'italic' }}>
                  {isMobileDevice() 
                    ? '🔊 Tap to hear pronunciation | Long press to practice'
                    : '🔊 Click to hear pronunciation | Double-click to practice'}
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Sentence Practice Mode - Removed */}

      {/* Word Practice Modal */}
      {/* Game Mode */}
      {practiceMode === 'game' && (
        <div className="game-section">
          <div style={{
            textAlign: 'center',
            marginBottom: '30px'
          }}>
            <h1 style={{
              fontSize: '2.5rem',
              color: '#2c3e50',
              marginBottom: '10px'
            }}>🎮 Games Arena</h1>
            <p style={{
              fontSize: '1.2rem',
              color: '#666'
            }}>Challenge yourself and compete with others!</p>
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '30px'
          }}>
            <Leaderboard />
            <HindiPronunciationQuest />
          </div>
        </div>
      )}

      {practiceWord && (
        <div className="word-practice-modal">
          <div className="modal-content">
            <button className="close-btn" onClick={closePracticeWord}>×</button>
            <h3>Practice Word: {practiceWord}</h3>
            <button className="play-word-btn">
              🔊 Play Word
            </button>
            <p>Click record to practice this word in isolation</p>
            <VoiceRecorder 
              onRecordingComplete={(blob, url) => {
                // Handle word-level recording
                console.log('Word practice recording:', practiceWord);
              }}
              targetSentence={practiceWord}
              hideLanguageSelector={true}
              hideTranscription={true}
              customPrompt="Speak the word clearly"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default HindiPracticeMode;